import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useState } from 'react'

interface AddTargetDialogProps {
    isOpen: boolean
    onClose: () => void
    onAdd: (target: any) => void
    isException?: boolean
}

export function AddTargetDialog({ isOpen, onClose, onAdd, isException = false }: AddTargetDialogProps) {
    const [targetNumber, setTargetNumber] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [description, setDescription] = useState('')
    const [utterance, setUtterance] = useState('')
    const [language, setLanguage] = useState('de')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleReset = () => {
        setTargetNumber('')
        setPhoneNumber('')
        setDescription('')
        setUtterance('')
        setLanguage('de')
    }

    const handleClose = () => {
        handleReset()
        onClose()
    }

    const handleAdd = async () => {
        if (!targetNumber || !phoneNumber || !utterance) return

        setIsSubmitting(true)
        try {
            await onAdd({
                targetNumber,
                phoneNumber,
                targetDescription: description,
                utteranceToSpeak: utterance,
                utteranceLanguage: language,
                deviceId: 'CALL_FORWARD',
                type: 'PHONE_NUMBER'
            })
            handleClose()
        } catch (error) {
            console.error('Error adding target:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const isFormValid = targetNumber && phoneNumber && utterance

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        {isException ? 'Exception-Weiterleitungsziel hinzufügen' : 'Weiterleitungsziel hinzufügen'}
                    </DialogTitle>
                    <DialogDescription>
                        Fügen Sie ein neues Weiterleitungsziel hinzu
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="targetNumber">Zielnummer *</Label>
                        <Input
                            id="targetNumber"
                            value={targetNumber}
                            onChange={(e) => setTargetNumber(e.target.value)}
                            placeholder="z.B. 1"
                            type="number"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="phoneNumber">Telefonnummer *</Label>
                        <Input
                            id="phoneNumber"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="+49..."
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Beschreibung</Label>
                        <Input
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="utterance">Ansagetext *</Label>
                        <Textarea
                            id="utterance"
                            value={utterance}
                            onChange={(e) => setUtterance(e.target.value)}
                            placeholder="Text der Ansage"
                            className="min-h-[100px]"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="language">Sprache</Label>
                        <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger id="language">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="de">Deutsch</SelectItem>
                                <SelectItem value="en">English</SelectItem>
                                <SelectItem value="fr">Français</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleAdd}
                        disabled={!isFormValid || isSubmitting}
                        className="bg-brand-primary hover:bg-brand-primary/90"
                    >
                        {isSubmitting ? 'Wird hinzugefügt...' : 'Hinzufügen'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
